import { Divider, makeStyles } from '@material-ui/core';
import { AnimatePresence, motion } from 'framer-motion';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useTypedSelector } from '../../../redux';
import { theme } from '../../../theme/theme';
import { Button, motions } from '../../global';
import { ProjectScheduler } from '../scheduler/ProjectScheduler';

const mod = 'editor.EditorScheduler';

const useEditorSchedulerClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        color: theme.colors.darkBlue[5],
        '& > span:first-child': {
            color: theme.colors.darkBlue[5],
            ...theme.typography.headings.h3,
            marginBottom: 0,
        },
        '& > hr:first-of-type': {
            background: theme.colors.neutral[3],
            margin: '10px 0',
        },
        '& > hr:last-of-type': {
            background: theme.colors.neutral[3],
            margin: '10px 0 20px 0',
        },
    },
    scheduler: {
        display: 'flex',
        flexDirection: 'column',
        flex: '1 1 auto',
        height: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
    },
    footer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        '& > span': {
            ...theme.typography.content.caption.default,
        },
    },
});

/**
 * Wrapper around the default project scheduler component that adds a custom header and navigation buttons.
 *
 * @param props.onComplete callback triggered when the view project or back button is clicked after scheduling
 */
export const EditorScheduler = (props: { onComplete: () => void }) => {
    const classes = useEditorSchedulerClasses();
    const { t } = useTranslation(['project']);
    const { project } = useTypedSelector(state => ({ project: state.project.changed }));
    const [next, setNext] = React.useState<() => void>();
    const [steps, setSteps] = React.useState(0);
    const [step, setStep] = React.useState(0);
    const [unlocked, setUnlocked] = React.useState(false);

    if (!project) return <span>{'#project not in store'}</span>;

    return (
        <div className={classes.root}>
            <AnimatePresence exitBeforeEnter>
                {step < steps && (
                    <motion.span key={step} {...motions.presence({ slide: 10 }, true)}>
                        {t(`${mod}.${step === 0 ? 'details' : step === 1 ? 'schedule' : 'tasks'}`)}
                    </motion.span>
                )}
            </AnimatePresence>
            <AnimatePresence>{step < steps && <Divider />}</AnimatePresence>
            <div className={classes.scheduler}>
                <ProjectScheduler
                    onStart={(next, steps) => (setNext(() => next), setSteps(steps))}
                    onStep={setStep}
                    onUnlocked={setUnlocked}
                    completeActions={[
                        { name: t(`${mod}.button-download`), onClick: () => {}, primary: true },
                        { name: t(`${mod}.button-view`), onClick: props.onComplete },
                        { name: t(`${mod}.button-back`), onClick: props.onComplete, primary: true, fail: true },
                    ]}
                />
            </div>
            <AnimatePresence>{step < steps && <Divider />}</AnimatePresence>
            <AnimatePresence>
                {step < steps && (
                    <motion.div className={classes.footer} {...motions.presence({ slide: 10 })}>
                        <span>{t(`${mod}.step`, { step: step + 1, steps })}</span>
                        <Button variant='contained' disabled={!unlocked} onClick={next}>
                            {t(
                                `${mod}.button-${
                                    step + 1 < steps ? 'continue' : project?.id === -1 ? 'schedule' : 'reschedule'
                                }`,
                            )}
                        </Button>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};
